/**
 * Константы ИИ-апскейла. Откуда взялись цифры — `.claude/docs/image-upscale.md`,
 * разделы «Chosen thresholds», «Model files» и «Speed».
 */

/**
 * Род картинки: от него зависят и модель, и порог «мелкая».
 *
 * Модель для плоской графики делает лица «нарисованными», модель для фото оставляет
 * на логотипе шум JPEG и ореолы у букв — поэтому род задаёт вызывающая сторона,
 * кит его не угадывает.
 */
export type ImageKind = 'graphic' | 'photo';

/**
 * Наибольшая сторона, начиная с которой картинка уже **не** мелкая.
 *
 * `graphic`: наибольший плейсхолдер логотипа 250 px × 1,5 на поля логотипа → 384.
 * `photo`: `carousel-avatar` табло 520×560, полей у кадрированного фото нет → 640.
 */
export const SMALL_IMAGE_MAX_SIDE: Record<ImageKind, number> = {
  graphic: 384,
  photo: 640,
};

/** Наибольшая сторона результата: порог × 1,33 под выход 1440p, округлено до 128. */
export const UPSCALE_TARGET_SIDE: Record<ImageKind, number> = {
  graphic: 512,
  photo: 768,
};

/**
 * Во сколько раз увеличивает модель рода — это свойство файла модели, не выбор.
 *
 * Typescale (`graphic`) обучена на ×8, а результат потом уменьшается до цели: так его
 * буквы и выходят чёткими. Итог относительно исходника от этого не меняется — его
 * зажимают `MIN_UPSCALE` и `MAX_UPSCALE`.
 */
export const MODEL_SCALE: Record<ImageKind, number> = {
  graphic: 8,
  photo: 4,
};

/** Меньше ×2 улучшать незачем: вышло бы то же, что у исходника, только тяжелее. */
export const MIN_UPSCALE = 2;

/** Больше ×4 от исходника не отдаём: дальше модель уже не восстанавливает, а дорисовывает. */
export const MAX_UPSCALE = 4;

/**
 * Сторона плитки и перекрытие с каждой стороны: на вход модели всегда идёт квадрат
 * 160×160. Постоянная форма тензора не заставляет WebGPU перекомпилировать шейдеры,
 * а перекрытие убирает швы между плитками. На этих размерах сняты замеры фазы 1.
 */
export const TILE_SIZE = 128;
export const TILE_PADDING = 16;

const PUBLIC_URL = process.env.PUBLIC_URL ?? '';

/**
 * Модели в ONNX: `graphic` — 8x-NMKD-Typescale (WTFPL, fp16, наш экспорт), `photo` —
 * Real-ESRGAN general-x4v3 (BSD-3-Clause). Лежат в `public/models/`, раздаются нашим
 * nginx и грузятся только та, что нужна полю, и только по кнопке.
 */
export const MODEL_URL: Record<ImageKind, string> = {
  graphic: `${PUBLIC_URL}/models/nmkd-typescale-x8-fp16.onnx`,
  photo: `${PUBLIC_URL}/models/realesr-general-x4v3.onnx`,
};

/**
 * Рантайм ONNX Runtime: файлы кладёт `scripts/copy-ort-wasm.js`. JS-обвязка лежит с
 * расширением `.js`, а не `.mjs`, как в пакете: nginx не знает `.mjs` и отдаёт его не
 * JavaScript-типом, а такой модуль браузер исполнять отказывается. Поэтому адреса заданы
 * поштучно, а не каталогом — по каталогу ORT сам достроил бы имя `.mjs`.
 */
export const ORT_WASM_FILES = {
  mjs: `${PUBLIC_URL}/ort/ort-wasm-simd-threaded.asyncify.js`,
  wasm: `${PUBLIC_URL}/ort/ort-wasm-simd-threaded.asyncify.wasm`,
};

/** Имена входа и выхода у обеих моделей. */
export const MODEL_INPUT = 'input';
export const MODEL_OUTPUT = 'output';

/**
 * Секунд на плитку без WebGPU — однопоточный WASM (`.claude/docs/image-upscale.md`, «Speed»;
 * замер на Ryzen 9 9900X3D, то есть лучший случай). По ним модалка заранее пишет, сколько
 * ждать: Typescale без видеокарты считает логотип 383 px около 4 минут.
 */
export const WASM_SECONDS_PER_TILE: Record<ImageKind, number> = {
  graphic: 26,
  photo: 1.4,
};
